import React from 'react';
import { UploadImageIcon, BubbleChatQuestionIcon } from '@assets/icon';
import './CskhPlusDrawer.scss';

export type CskhPlusActionKey = 'image' | 'report-bug' | 'suggest-improve' | 'consult-feature';

export interface CskhPlusActionItem {
  key: CskhPlusActionKey;
  label: string;
}

interface CskhPlusDrawerProps {
  open: boolean;
  onClose: () => void;
  onAction: (key: CskhPlusActionKey) => void;
  buttonRef?: React.RefObject<HTMLElement | null>;
}

interface ItemProps {
  action: CskhPlusActionItem;
  onClick?: (key: CskhPlusActionKey) => void;
}

interface DismissParams {
  open: boolean;
  menuRef: React.RefObject<HTMLElement | null>;
  buttonRef?: React.RefObject<HTMLElement | null>;
  onClose?: () => void;
}

const ACTIONS: CskhPlusActionItem[] = [
  { key: 'image', label: 'Hình ảnh' },
  { key: 'report-bug', label: 'Báo lỗi' },
  { key: 'suggest-improve', label: 'Gợi ý cải thiện sp' },
  { key: 'consult-feature', label: 'Tư vấn tính năng mới' },
];

const renderActionIcon = (key: CskhPlusActionKey) => {
  if (key === 'image') return <UploadImageIcon />;
  return <BubbleChatQuestionIcon />;
};

const CskhActionItem: React.FC<ItemProps> = ({ action, onClick }) => {
  if (!action) return null;
  return (
    <button type="button" className="cskh-plus-menu__item" onClick={() => onClick?.(action.key)}>
      <span className="cskh-plus-menu__item-icon">{renderActionIcon(action.key)}</span>
      <span className="cskh-plus-menu__item-label">{action.label}</span>
    </button>
  );
};

const isClickOutside = (target: Node, menu?: HTMLElement | null, btn?: HTMLElement | null) => {
  if (menu?.contains(target)) return false;
  if (btn?.contains(target)) return false;
  if ((target as Element)?.closest?.('.input-message-plus-btn')) return false;
  return true;
};

const createOutsideHandler = (
  menu: HTMLElement | null,
  btn: HTMLElement | null | undefined,
  onClose?: () => void
) => (e: MouseEvent) => {
  const target = e.target as Node | null;
  if (target && isClickOutside(target, menu, btn)) onClose?.();
};

const createKeyHandler = (onClose?: () => void) => (e: KeyboardEvent) => {
  if (e.key === 'Escape') onClose?.();
};

const useCskhMenuDismiss = ({ open, menuRef, buttonRef, onClose }: DismissParams) => {
  React.useEffect(() => {
    if (!open) return;
    const onMouseDown = createOutsideHandler(menuRef?.current ?? null, buttonRef?.current, onClose);
    const onKeyDown = createKeyHandler(onClose);
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, menuRef, buttonRef, onClose]);
};

const CskhPlusDrawer: React.FC<CskhPlusDrawerProps> = ({ open, onClose, onAction, buttonRef }) => {
  const menuRef = React.useRef<HTMLDivElement>(null);
  useCskhMenuDismiss({ open, menuRef, buttonRef, onClose });

  if (!open) return null;
  return (
    <div ref={menuRef} className="cskh-plus-menu cskh-plus-drawer" role="menu" aria-label="Thêm nội dung CSKH">
      {ACTIONS.map((action) => (
        <CskhActionItem key={action.key} action={action} onClick={onAction} />
      ))}
    </div>
  );
};

export default CskhPlusDrawer;
